// Types
import type { ImageProps } from 'next/image';

import type { Translate } from '@/utils/hooks/translation/useTranslation';

import type {
    CompanyPeopleCountType,
    HearAboutUsType,
    IsAgencyDetailType,
    PrimaryGoalType,
    SocialMediaToolType,
    SocialProfileManagedType,
    TeamSizeType
} from '@/types/userMarketingType';

// Images
import chart_increasing from './../../../../public/images/signup/emojis/chart_increasing.webp';
import eyes from './../../../../public/images/signup/emojis/eyes.webp';
import hand_with_fingers_splayed from './../../../../public/images/signup/emojis/hand_with_fingers_splayed.webp';
import handshake from './../../../../public/images/signup/emojis/handshake.webp';
import index_pointing_up from './../../../../public/images/signup/emojis/index_pointing_up.webp';
import person_gesturing_no from './../../../../public/images/signup/emojis/person_gesturing_no.webp';
import raising_hands from './../../../../public/images/signup/emojis/raising_hands.webp';
import technologist from './../../../../public/images/signup/emojis/technologist.webp';
import victory_hand from './../../../../public/images/signup/emojis/victory_hand.webp';
import woman_technologist from './../../../../public/images/signup/emojis/woman_technologist.webp';

export type PrimaryGoalOptionType = Pick<ImageProps, 'src'> & {
    label: string;
    value: PrimaryGoalType;
};

export const primary_goal = (t: Translate): PrimaryGoalOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_1.primary_goal.options.reach_out'),
            value: 'reach_out',
            src: handshake
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_1.primary_goal.options.implement_listening'
            ),
            value: 'implement_listening',
            src: eyes
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_1.primary_goal.options.streamline_automate'
            ),
            value: 'streamline_automate',
            src: woman_technologist
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_1.primary_goal.options.analyze_performance'
            ),
            value: 'analyze_performance',
            src: chart_increasing
        }
    ] as const;

export type HearAboutUsOptionType = {
    label: string;
    value: HearAboutUsType;
};

export const hear_about_us = (t: Translate): HearAboutUsOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.word_of_mouth'),
            value: 'word_of_mouth'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.social_media'),
            value: 'social_media'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.search_engine'),
            value: 'search_engine'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.advertising'),
            value: 'advertising'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.podcast'),
            value: 'podcast'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.blog'),
            value: 'blog'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.sos_2024'),
            value: 'sos_2024'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_1.hear_about_us.options.other'),
            value: 'other'
        }
    ] as const;

export type TeamSizeOptionType = Pick<ImageProps, 'src'> & {
    label: string;
    subLabel: string;
    value: TeamSizeType;
};

export const team_size = (t: Translate): TeamSizeOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_2.team_size.options.1.title'),
            subLabel: t('signup_funnel:user_infos.sub_step_2.team_size.options.1.description'),
            value: '1',
            src: index_pointing_up
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.team_size.options.2-3.title'),
            subLabel: t('signup_funnel:user_infos.sub_step_2.team_size.options.2-3.description'),
            value: '2-3',
            src: victory_hand
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.team_size.options.4-10.title'),
            subLabel: t('signup_funnel:user_infos.sub_step_2.team_size.options.4-10.description'),
            value: '4-10',
            src: hand_with_fingers_splayed
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.team_size.options.11+.title'),
            subLabel: t('signup_funnel:user_infos.sub_step_2.team_size.options.11+.description'),
            value: '11+',
            src: raising_hands
        }
    ] as const;

export type SocialProfileManagedOptionType = {
    label: string;
    value: SocialProfileManagedType;
};

export const social_profiles_managed = (t: Translate): SocialProfileManagedOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_profiles_managed.options.1'),
            value: '1'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_profiles_managed.options.2-5'),
            value: '2-5'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_profiles_managed.options.6-10'),
            value: '6-10'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_profiles_managed.options.11-20'),
            value: '11-20'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_profiles_managed.options.21-50'),
            value: '21-50'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_profiles_managed.options.51+'),
            value: '51+'
        }
    ] as const;

export type SocialMediaToolOptionType = {
    label: string;
    value: SocialMediaToolType;
};

export const social_media_tool = (t: Translate): SocialMediaToolOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_media_tool.options.no_tools'),
            value: 'no_tools'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_media_tool.options.sprout'),
            value: 'sprout'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_media_tool.options.hootsuite'),
            value: 'hootsuite'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_2.social_media_tool.options.other'),
            value: 'other'
        }
    ] as const;

export type CompanyPeopleCountOptionType = {
    label: string;
    value: CompanyPeopleCountType;
};

export const company_people_count = (t: Translate): CompanyPeopleCountOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.1'),
            value: '1'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.2-10'),
            value: '2-10'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.11-50'),
            value: '11-50'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.51-200'),
            value: '51-200'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.201-500'),
            value: '201-500'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.501-1000'),
            value: '501-1000'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.1001-5000'),
            value: '1001-5000'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.5001-10000'),
            value: '5001-10000'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.company_people_count.options.10001+'),
            value: '10001+'
        }
    ] as const;

export type IsAgencyOptionType = Pick<ImageProps, 'src'> & {
    label: string;
    value: 'yes' | 'no';
};

export const is_agency = (t: Translate): IsAgencyOptionType[] =>
    [
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency.options.yes'),
            value: 'yes',
            src: technologist
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency.options.no'),
            value: 'no',
            src: person_gesturing_no
        }
    ] as const;

export type IsAgencyDetailOptionType = {
    label: string;
    value: IsAgencyDetailType;
};

export const is_agency_detail = (t: Translate): IsAgencyDetailOptionType[] =>
    [
        {
            label: t(
                'signup_funnel:user_infos.sub_step_3.is_agency_detail.options.media_entertainment'
            ),
            value: 'media_entertainment'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency_detail.options.fashion_beauty'),
            value: 'fashion_beauty'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency_detail.options.food_beverage'),
            value: 'food_beverage'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency_detail.options.travel_leasure'),
            value: 'travel_leasure'
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_3.is_agency_detail.options.health_wellness_sports'
            ),
            value: 'health_wellness_sports'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency_detail.options.lifestyle'),
            value: 'lifestyle'
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_3.is_agency_detail.options.financial_services_insurance'
            ),
            value: 'financial_services_insurance'
        },
        {
            label: t('signup_funnel:user_infos.sub_step_3.is_agency_detail.options.technology'),
            value: 'technology'
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_3.is_agency_detail.options.public_educational_ngo'
            ),
            value: 'public_educational_ngo'
        },
        {
            label: t(
                'signup_funnel:user_infos.sub_step_3.is_agency_detail.options.other_b2c_b2b_business'
            ),
            value: 'other_b2c_b2b_business'
        }
    ] as const;
